import { atom, Getter } from "jotai";
import { operatingGrowthCapExTotalMolecule } from "../growthCapEx";
import {
  amortOfDealExpensesMolecule,
  annualMaintenanceCapExMolecule,
  buyersCompMolecule,
  companySaleDebtPayoffMolecule,
  companysTaxRateMolecule,
  costOfGoodsSoldItemization,
  depreciationAndAmortizationMolecule,
  distributableFreeCashFlowMolecule,
  dscrMolecule,
  ebitdaMarginsMolecule,
  ebitdaMolecule,
  goodwillDepreciationAndAmortizationMolecule,
  grossMarginsMolecule,
  grossProfitMolecule,
  grossRevenueItemization,
  incomeTaxesMolecule,
  lineOfCreditPaymentMolecule,
  netIncomeMolecule,
  operatingExpensesItemization,
  operatingIncomeMolecule,
  proceedsFromCompanySaleMolecule,
  sba7aLoanExpenseInterestPaymentMolecule,
  sba7aLoanPaymentMolecule,
  sellersNoteExpenseInterestPaymentMolecule,
  sellersNoteLoanPaymentMoleculeWithBalloon,
  taxableIncomeMolecule,
  totalDebtServiceMolecule,
  totalEquityInvestorsDistributionsMolecule,
  totalEquityInvestorsProceedsFromCompanySaleMolecule,
  totalEquityPreferredDividendsMolecule,
  totalEquityPrincipalRepaymentsMolecule,
  totalEquityProfitDistributionsMolecule,
  unleveredFreeCashFlowMolecule,
  useableFreeCashFlowMolecule,
  yearsFromSbaLoanStartDateAtom,
} from "../projections";
import {
  boldStyle,
  createEmptyRow,
  createGroupEndRow,
  createGroupStartRow,
  createTitleRow,
  doubleTopBorderStyle,
  greyHighlightStyle,
  groupWorksheetRows,
  italicStyle,
  itemizationToRows,
  moleculeToRow,
  singleTopBorderStyle,
  toSpreadsheetCell,
} from "./helper";

const XLSXStub = () => import("xlsx-js-style");

type XLSXModule = Awaited<ReturnType<typeof XLSXStub>>;

export const buildProjectionsWorksheet = (get: Getter, XLSX: XLSXModule) => {
  const pageTitle = (window.document.title || "ETA IQ Projections")
    .replace("Model (", "")
    .replace(")", "");

  const rows = [
    [pageTitle],
    ["Created on - " + new Date().toLocaleString()],
    createEmptyRow(),
    ["DSCR", ...get(dscrMolecule)].map((cell) =>
      toSpreadsheetCell(get, cell, { type: "dscr" }),
    ),

    createEmptyRow(),
    createTitleRow("COMPANY FINANCIALS"),
    createEmptyRow(),
    [
      undefined,
      "Year 0",
      undefined,
      ...get(yearsFromSbaLoanStartDateAtom).map((_year, i) => `Year ${i + 1}`),
    ],

    [
      undefined,
      get(yearsFromSbaLoanStartDateAtom)[0] - 1,
      "", // CAGR
      ...get(yearsFromSbaLoanStartDateAtom),
    ],
    createEmptyRow(),

    ...itemizationToRows(get, "Gross Revenue", grossRevenueItemization),

    ...itemizationToRows(
      get,
      "Cost of Goods Sold (COGS)",
      costOfGoodsSoldItemization,
      { isNegative: true },
    ),

    moleculeToRow(get, grossProfitMolecule, {
      label: "Gross Profit",
      style: doubleTopBorderStyle,
    }),

    createEmptyRow(),
    moleculeToRow(get, grossMarginsMolecule, {
      label: "Gross Margins (%)",
      type: "percent",
      style: italicStyle,
    }),
    createEmptyRow(),
    createEmptyRow(),

    moleculeToRow(get, grossProfitMolecule, {
      label: "Gross Profit",
      style: boldStyle,
    }),
    ...itemizationToRows(
      get,
      "Operating Expenses",
      operatingExpensesItemization,
      {
        isNegative: true,
      },
    ),
    [
      "Buyer's Compensation",
      ...moleculeToRow(get, buyersCompMolecule, { isNegative: true }),
    ],

    moleculeToRow(get, ebitdaMolecule, {
      label: "EBITDA",
      style: doubleTopBorderStyle,
    }),
    createEmptyRow(),
    createEmptyRow(),
    moleculeToRow(get, ebitdaMarginsMolecule, {
      label: "EBITDA Margins (%)",
      type: "percent",
      style: italicStyle,
    }),
    createEmptyRow(),
    createEmptyRow(),

    moleculeToRow(get, ebitdaMolecule, { label: "EBITDA", style: boldStyle }),
    createGroupStartRow(),
    [
      `Goodwill (D & A)`,
      ...moleculeToRow(get, goodwillDepreciationAndAmortizationMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Deal Expenses (D & A)",
      ...moleculeToRow(get, amortOfDealExpensesMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Growth CapEx (D & A)",
      ...moleculeToRow(get, operatingGrowthCapExTotalMolecule, {
        isNegative: true,
      }),
    ],
    createGroupEndRow(),
    [
      "Depreciation & Amortization",
      ...moleculeToRow(get, depreciationAndAmortizationMolecule, {
        isNegative: true,
      }),
    ],

    moleculeToRow(get, operatingIncomeMolecule, {
      label: "Operating Income",
      style: doubleTopBorderStyle,
    }),

    createEmptyRow(),
    createEmptyRow(),

    moleculeToRow(get, operatingIncomeMolecule, {
      label: "Operating Income",
      style: boldStyle,
    }),

    createGroupStartRow(),
    [
      "Interest Expense - Senior Debt",
      ...moleculeToRow(get, sba7aLoanExpenseInterestPaymentMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Interest Expense - Seller's Note",
      ...moleculeToRow(get, sellersNoteExpenseInterestPaymentMolecule, {
        isNegative: true,
      }),
    ],

    moleculeToRow(get, taxableIncomeMolecule, {
      label: "Taxable Income",
      style: doubleTopBorderStyle,
    }),

    moleculeToRow(get, companysTaxRateMolecule, {
      label: "Company's Tax Rate",
      style: italicStyle,
      type: "percent",
    }),

    createGroupEndRow(),
    moleculeToRow(get, incomeTaxesMolecule, {
      isNegative: true,
      label: "Income Taxes",
      style: doubleTopBorderStyle,
    }),
    moleculeToRow(get, sba7aLoanExpenseInterestPaymentMolecule, {
      label: "Interest Expense - Senior Debt",
      isNegative: true,
    }),
    moleculeToRow(get, sellersNoteExpenseInterestPaymentMolecule, {
      label: "Interest Expense - Seller's Note",
      isNegative: true,
    }),

    moleculeToRow(get, netIncomeMolecule, {
      label: "Net Income",
      style: doubleTopBorderStyle,
    }),

    createEmptyRow(),
    createTitleRow(" DEBT SERVICE"),
    createEmptyRow(),

    [
      toSpreadsheetCell(get, "PRINCIPAL & INTEREST PAYMENTS:", {
        type: "string",
        style: boldStyle,
      }),
    ],

    moleculeToRow(get, sba7aLoanPaymentMolecule, {
      label: "Senior Debt (SBA 7(a) Loan)",
      style: singleTopBorderStyle,
    }),
    moleculeToRow(get, lineOfCreditPaymentMolecule, {
      label: "Line of Credit",
    }),
    [
      "Seller's Note",
      ...moleculeToRow(get, sellersNoteLoanPaymentMoleculeWithBalloon),
    ],

    moleculeToRow(get, totalDebtServiceMolecule, {
      label: "Total Debt Service",
      style: doubleTopBorderStyle,
    }),

    createEmptyRow(),
    createTitleRow("OWNER &  EQUITY INVESTOR DISTRIBUTIONS"),
    createEmptyRow(),

    createGroupStartRow(),
    moleculeToRow(get, ebitdaMolecule, {
      label: "EBITDA",
      style: boldStyle,
    }),
    moleculeToRow(get, incomeTaxesMolecule, {
      label: "Income Taxes",
      isNegative: true,
    }),

    moleculeToRow(get, operatingGrowthCapExTotalMolecule, {
      label: "Growth CapEx",
      isNegative: true,
    }),
    moleculeToRow(get, annualMaintenanceCapExMolecule, {
      label: "Maintenance CapEx",
      isNegative: true,
    }),
    moleculeToRow(get, proceedsFromCompanySaleMolecule, {
      label: "Gross Proceeds from Company Sale",
      isNegative: true,
    }),
    createGroupEndRow(),
    moleculeToRow(get, unleveredFreeCashFlowMolecule, {
      label: "Unlevered Free Cash Flow (Free Cash Flow to Lenders)",
      style: greyHighlightStyle,
    }),

    createEmptyRow(),

    createGroupStartRow(),
    moleculeToRow(get, unleveredFreeCashFlowMolecule, {
      label: "Unlevered Free Cash Flow (Free Cash Flow to Lenders)",
      style: boldStyle,
    }),
    moleculeToRow(get, totalDebtServiceMolecule, {
      label: "Regular Debt Service",
      isNegative: true,
    }),
    moleculeToRow(get, companySaleDebtPayoffMolecule, {
      label: "Company Sale Debt Payoff",
      isNegative: true,
    }),
    createGroupEndRow(),
    moleculeToRow(get, distributableFreeCashFlowMolecule, {
      label: "Distributable Free Cash Flow (Free Cash Flow to Equity Holders)",
      style: greyHighlightStyle,
    }),

    createEmptyRow(),

    createGroupStartRow(),
    moleculeToRow(get, distributableFreeCashFlowMolecule, {
      label: "Distributable Free Cash Flow (Free Cash Flow to Equity Holders)",
      style: boldStyle,
    }),
    [
      "Total Equity Investors Distributions",
      ...moleculeToRow(get, totalEquityInvestorsDistributionsMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Preferred Dividends",
      ...moleculeToRow(get, totalEquityPreferredDividendsMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Principal Payments (Return of Capital)",
      ...moleculeToRow(get, totalEquityPrincipalRepaymentsMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Profit Distributions (from Ops)",
      ...moleculeToRow(get, totalEquityProfitDistributionsMolecule, {
        isNegative: true,
      }),
    ],
    [
      "Proceeds from Company Sale",
      ...moleculeToRow(
        get,
        totalEquityInvestorsProceedsFromCompanySaleMolecule,
        { isNegative: true },
      ),
    ],
    createGroupEndRow(),
    moleculeToRow(get, useableFreeCashFlowMolecule, {
      label: "Usable Free Cash Flow (Free Cash Flow to Owner)",
      style: greyHighlightStyle,
    }),
  ];

  const rowGroups: { start: number; end: number }[] = [];
  const cleanedRows: typeof rows = [];
  let currentGroupStartIndex: number | null = null;

  rows.forEach((row) => {
    if (row[0] === "GROUP_START") {
      currentGroupStartIndex = cleanedRows.length;
    } else if (row[0] === "GROUP_END") {
      if (currentGroupStartIndex !== null) {
        rowGroups.push({
          start: currentGroupStartIndex,
          end: cleanedRows.length - 1,
        });
        currentGroupStartIndex = null;
      }
    } else {
      cleanedRows.push(row);
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(cleanedRows);

  const COL_WIDTH = 225;
  const COL_INDEX = 0;
  if (!ws["!cols"]) ws["!cols"] = [];
  if (!ws["!cols"][COL_INDEX]) ws["!cols"][COL_INDEX] = { wch: 8 };
  ws["!cols"][COL_INDEX].wpx = COL_WIDTH;

  rowGroups.forEach(({ start, end }) => groupWorksheetRows(ws, start, end));

  return ws;
};

export const projectionsExportAtom = atom(null, async (get) => {
  const XLSX = await XLSXStub();
  const ws = buildProjectionsWorksheet(get, XLSX);

  // Export
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Projections");

  const fmt = "xlsx";
  const filename = `projections-${new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "")}.${fmt}`;

  XLSX.writeFile(wb, filename);

  return;
});
